/**
 * Plain descriptions of what a quota resource limits, for the tooltip on each
 * dial. The compute entries are the Kubernetes documentation's own wording.
 *
 * https://kubernetes.io/docs/concepts/policy/resource-quotas/#types-of-resource-quota
 */
const DEFINITIONS: Record<string, string> = {
  'limits.cpu':
    'Across all pods in a non-terminal state, the sum of CPU limits cannot exceed this value.',
  'limits.memory':
    'Across all pods in a non-terminal state, the sum of memory limits cannot exceed this value.',
  'requests.cpu':
    'Across all pods in a non-terminal state, the sum of CPU requests cannot exceed this value.',
  'requests.memory':
    'Across all pods in a non-terminal state, the sum of memory requests cannot exceed this value.',
  cpu: 'Across all pods in a non-terminal state, the sum of CPU requests cannot exceed this value.',
  memory:
    'Across all pods in a non-terminal state, the sum of memory requests cannot exceed this value.',
  pods: 'The number of pods in a non-terminal state that can exist in the namespace.',
  'requests.storage':
    'Across all persistent volume claims, the sum of storage requests cannot exceed this value.',
  persistentvolumeclaims:
    'The number of persistent volume claims that can exist in the namespace.',
  'services.loadbalancers':
    'The number of services of type LoadBalancer that can exist in the namespace.',
  'services.nodeports':
    'The number of node ports held by services in the namespace.',
  services: 'The number of services that can exist in the namespace.',
};

const HUGEPAGES =
  'Across all pods in a non-terminal state, the number of huge page requests of this size cannot exceed this value.';

const STORAGE_CLASS_SUFFIX = '.storageclass.storage.k8s.io/requests.storage';
const STORAGE_CLASS_CLAIMS_SUFFIX =
  '.storageclass.storage.k8s.io/persistentvolumeclaims';

/** What this resource limits, in a sentence, or undefined when we have none. */
export function resourceQuotaDefinition(resource: string): string | undefined {
  if (DEFINITIONS[resource]) {
    return DEFINITIONS[resource];
  }

  if (resource.startsWith('hugepages-')) {
    return HUGEPAGES;
  }

  if (resource.endsWith(STORAGE_CLASS_SUFFIX)) {
    return `Across all persistent volume claims bound to the ${storageClassOf(resource, STORAGE_CLASS_SUFFIX)} storage class, the sum of storage requests cannot exceed this value.`;
  }

  if (resource.endsWith(STORAGE_CLASS_CLAIMS_SUFFIX)) {
    return `The number of persistent volume claims bound to the ${storageClassOf(resource, STORAGE_CLASS_CLAIMS_SUFFIX)} storage class that can exist in the namespace.`;
  }

  if (resource.startsWith('count/')) {
    return `The number of ${resource.slice('count/'.length)} objects that can exist in the namespace.`;
  }

  return undefined;
}

function storageClassOf(resource: string, suffix: string) {
  return resource.slice(0, -suffix.length);
}
